import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../components/Modal';
import type { SectionInfo } from '../types';
import { useAdmin } from './context';

interface Props {
  /** null = create mode */
  section: SectionInfo | null;
  /** number of forms currently in this section (any status) — delete only at 0 */
  formsCount: number;
  onClose: () => void;
  onSaved: (section: SectionInfo, isNew: boolean) => void;
  onDeleted: (key: string) => void;
}

const inputCls =
  'w-full rounded-xl border border-border bg-input px-3.5 py-2.5 text-sm outline-none transition-colors placeholder:text-muted/70 focus:border-brand';
const labelCls = 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted';

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function SectionEditor({ section, formsCount, onClose, onSaved, onDeleted }: Props) {
  const { api } = useAdmin();
  const isEdit = section !== null;

  const [label, setLabel] = useState(section?.label ?? '');
  const [key, setKey] = useState(section?.key ?? '');
  const [keyTouched, setKeyTouched] = useState(isEdit);
  const [description, setDescription] = useState(section?.description ?? '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-derive the key from the label until the user edits it manually.
  useEffect(() => {
    if (!keyTouched) setKey(slugify(label));
  }, [label, keyTouched]);

  const keyValid = /^[a-z0-9]+(-[a-z0-9]+)*$/.test(key) && key.length >= 2 && key.length <= 30;
  const urlPreview = useMemo(() => `forms.knocking.com/${key || '…'}`, [key]);
  const canSave = label.trim().length > 0 && (isEdit || keyValid) && !saving;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const desc = description.trim();
      const result = isEdit
        ? await api.updateSection(section.key, { label: label.trim(), description: desc ? desc : null })
        : await api.createSection({ key, label: label.trim(), ...(desc ? { description: desc } : {}) });
      onSaved(result.section, !isEdit);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!section) return;
    setDeleting(true);
    setError(null);
    try {
      await api.deleteSection(section.key);
      onDeleted(section.key);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <Modal title={isEdit ? `Edit section: ${section.label}` : 'New section'} onClose={onClose}>
      <div className="flex flex-col gap-5">
        <div>
          <label htmlFor="se-label" className={labelCls}>Display label</label>
          <input
            id="se-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Legal"
            maxLength={60}
            className={inputCls}
          />
        </div>

        {!isEdit && (
          <div>
            <label htmlFor="se-key" className={labelCls}>URL key</label>
            <input
              id="se-key"
              value={key}
              onChange={(e) => {
                setKey(e.target.value);
                setKeyTouched(true);
              }}
              placeholder="legal"
              autoComplete="off"
              spellCheck={false}
              className={inputCls}
            />
            <p className="mt-1.5 text-xs text-muted">
              Public URL: <code className="font-mono text-foreground">{urlPreview}</code> — fixed after
              creation.
            </p>
          </div>
        )}

        <div>
          <label htmlFor="se-desc" className={labelCls}>
            Description <span className="font-normal normal-case">(optional)</span>
          </label>
          <textarea
            id="se-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            maxLength={200}
            placeholder="Shown on the public section page under the title."
            className={`${inputCls} resize-y`}
          />
        </div>

        {error && (
          <p role="alert" className="rounded-xl border border-danger/40 bg-danger/10 px-4 py-2.5 text-sm text-danger">
            {error}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <div>
            {isEdit && formsCount === 0 && (
              <button
                type="button"
                onClick={() => (confirmDelete ? void remove() : setConfirmDelete(true))}
                disabled={deleting}
                className="rounded-full border border-danger/40 px-4 py-2 text-sm font-medium text-danger transition-colors hover:bg-danger/10 disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : confirmDelete ? 'Click again to delete' : 'Delete section'}
              </button>
            )}
            {isEdit && formsCount > 0 && (
              <p className="text-xs text-muted">
                Sections with forms can't be deleted ({formsCount} form{formsCount === 1 ? '' : 's'}).
              </p>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-input"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={!canSave}
              className="rounded-full bg-brand px-5 py-2 text-sm font-semibold text-brand-contrast transition-colors hover:bg-brand-strong disabled:opacity-50"
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create section'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
